import { useState, useEffect } from "react";
import { Loader2, ShoppingBag, X } from "lucide-react";
import { toast } from "sonner";
import { PageHeader } from "../components/PageHeader";
import { Reveal } from "../lib/motion";
import { resolveImg, api } from "../lib/api";
import { useCart } from "../lib/cart";

function useProducts() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/store/products").then((r) => setData(r.data)).catch(() => setData([])); }, []);
  return data;
}

const price = (p) => (p.sale_price && Number(p.sale_price) > 0 ? Number(p.sale_price) : Number(p.price || 0));

export default function Store() {
  const products = useProducts();
  const { add } = useCart();
  const [sel, setSel] = useState(null);
  const [size, setSize] = useState("");
  const [color, setColor] = useState("");
  const [qty, setQty] = useState(1);

  const openProduct = (p) => { setSel(p); setSize((p.sizes || [])[0] || ""); setColor((p.colors || [])[0] || ""); setQty(1); };
  const addToCart = () => {
    if ((sel.sizes || []).length > 0 && !size) { toast.error("Please choose a size"); return; }
    add({ type: "product", id: sel.id, name: sel.name, price: price(sel), image: sel.image || (sel.images || [])[0], size, color, qty });
    setSel(null);
    toast.success("Added to cart");
  };

  return (
    <div data-testid="store-page">
      <PageHeader eyebrow="One Love Store" title={"Wear the\nMessage"} sub="Shirts, hoodies, hats, and church merchandise. Every purchase supports the ministry of One Love." />
      <section className="py-24 sm:py-32 bg-white">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8">
          {!products && <div className="py-20 grid place-items-center"><Loader2 className="animate-spin text-[#E63946]" size={32} /></div>}
          {products && products.length === 0 && (
            <div className="py-20 text-center text-black/50">
              <ShoppingBag className="mx-auto mb-4 text-[#E63946]" size={36} />
              <p className="font-display font-bold text-2xl mb-2 text-black">Store Opening Soon</p>
              <p>New One Love Ministries merchandise is on the way. Check back shortly!</p>
            </div>
          )}
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {(products || []).map((p, i) => (
              <Reveal key={p.id} delay={(i % 4) * 0.06}>
                <button onClick={() => openProduct(p)} data-testid={`product-card-${i}`}
                  className="group text-left w-full border border-black/10 rounded-sm overflow-hidden hover:border-[#E63946] transition-colors">
                  <div className="aspect-square bg-[#F4F4F5] overflow-hidden relative">
                    {(p.image || (p.images || [])[0]) && <img src={resolveImg(p.image || p.images[0])} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />}
                    {p.sold_out && <span className="absolute top-3 left-3 bg-[#0A0A0A] text-white text-xs px-3 py-1.5 rounded-full">Sold Out</span>}
                    {!p.sold_out && p.sale_price && Number(p.sale_price) > 0 && <span className="absolute top-3 left-3 bg-[#E63946] text-white text-xs px-3 py-1.5 rounded-full">Sale</span>}
                  </div>
                  <div className="p-4">
                    {p.category && <span className="text-[11px] uppercase tracking-[0.18em] text-[#E63946]">{p.category}</span>}
                    <p className="font-display font-bold text-lg leading-tight mt-0.5">{p.name}</p>
                    <p className="mt-1 text-sm">
                      <span className="font-bold text-[#E63946]">${price(p).toFixed(2)}</span>
                      {p.sale_price && Number(p.sale_price) > 0 && <span className="text-black/35 line-through ml-2">${Number(p.price).toFixed(2)}</span>}
                    </p>
                  </div>
                </button>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {sel && (
        <div className="fixed inset-0 z-50 bg-black/70 grid place-items-center p-4 overflow-y-auto" onClick={() => setSel(null)}>
          <div className="bg-white rounded-2xl w-full max-w-2xl my-8 overflow-hidden" onClick={(e) => e.stopPropagation()} data-testid="product-modal">
            <div className="grid sm:grid-cols-2">
              <div className="aspect-square bg-[#F4F4F5]">
                {(sel.image || (sel.images || [])[0]) && <img src={resolveImg(sel.image || sel.images[0])} alt={sel.name} className="w-full h-full object-cover" />}
              </div>
              <div className="p-6 flex flex-col">
                <div className="flex justify-between items-start">
                  <div>
                    {sel.category && <span className="text-[11px] uppercase tracking-[0.18em] text-[#E63946]">{sel.category}</span>}
                    <h3 className="font-display font-bold text-2xl leading-tight">{sel.name}</h3>
                  </div>
                  <button onClick={() => setSel(null)} className="grid place-items-center w-9 h-9 rounded-full hover:bg-black/5 shrink-0"><X size={18} /></button>
                </div>
                <p className="mt-2 text-lg">
                  <span className="font-bold text-[#E63946]">${price(sel).toFixed(2)}</span>
                  {sel.sale_price && Number(sel.sale_price) > 0 && <span className="text-black/35 line-through ml-2 text-sm">${Number(sel.price).toFixed(2)}</span>}
                </p>
                {sel.description && <p className="text-sm text-black/60 mt-3 leading-relaxed">{sel.description}</p>}
                {(sel.sizes || []).length > 0 && (
                  <div className="mt-4">
                    <span className="block text-xs uppercase tracking-[0.18em] text-black/50 mb-2">Size</span>
                    <div className="flex flex-wrap gap-2">
                      {sel.sizes.map((s) => (
                        <button key={s} onClick={() => setSize(s)} data-testid={`size-${s}`}
                          className={`px-3.5 py-1.5 rounded-full text-sm border transition-colors ${size === s ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "border-black/15 hover:border-black/40"}`}>{s}</button>
                      ))}
                    </div>
                  </div>
                )}
                {(sel.colors || []).length > 0 && (
                  <div className="mt-4">
                    <span className="block text-xs uppercase tracking-[0.18em] text-black/50 mb-2">Color</span>
                    <div className="flex flex-wrap gap-2">
                      {sel.colors.map((c) => (
                        <button key={c} onClick={() => setColor(c)}
                          className={`px-3.5 py-1.5 rounded-full text-sm border transition-colors ${color === c ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "border-black/15 hover:border-black/40"}`}>{c}</button>
                      ))}
                    </div>
                  </div>
                )}
                <div className="mt-5 flex items-center gap-3">
                  <button onClick={() => setQty(Math.max(1, qty - 1))} className="w-9 h-9 rounded-full border border-black/15">−</button>
                  <span className="font-display font-bold text-lg w-6 text-center">{qty}</span>
                  <button onClick={() => setQty(qty + 1)} className="w-9 h-9 rounded-full border border-black/15">+</button>
                </div>
                <div className="mt-auto pt-5">
                  {sel.sold_out ? (
                    <span className="block text-center rounded-full bg-black/10 text-black/50 py-3.5 text-sm font-medium">Sold Out</span>
                  ) : (
                    <button onClick={addToCart} data-testid="add-to-cart-btn"
                      className="w-full rounded-full bg-[#E63946] text-white py-3.5 font-medium hover:bg-[#BA1B25] transition-colors">Add to Cart — ${(price(sel) * qty).toFixed(2)}</button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
